import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Rectangle, Popup, useMap, useMapEvent, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import io from 'socket.io-client';
import '../styles/Map.css';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

// Area delimitata (Milano)
const AREA_BOUNDS = [
  //PARCO SEMPIONE
  // [45.47864361837631, 9.167542052587795], // Nord-Ovest
  // [45.46735830008697, 9.184364866632095]  // Sud-Est

  [47.78625069227117, 5.1682400949175475],
  [35.998935595511114, 19.933864307514884]
];

// Aggiungi un po' di padding all'area per una migliore esperienza utente
const BOUNDS_PADDING = 0.01; // circa 1km di padding
const MAX_BOUNDS = [
  [AREA_BOUNDS[0][0] + BOUNDS_PADDING, AREA_BOUNDS[0][1] - BOUNDS_PADDING], // Nord-Ovest con padding
  [AREA_BOUNDS[1][0] - BOUNDS_PADDING, AREA_BOUNDS[1][1] + BOUNDS_PADDING]  // Sud-Est con padding
];

// PAX PARCO SEMPIONE
const FIXED_MARKERS = [
  // [45.474009, 9.173448], //P1
  // [45.47427090015754, 9.173699672778659], //P2
  // [45.474530, 9.174069], //P3
  // [45.47433271153275, 9.17422314151853], //P4
  // [45.47414302801737, 9.173946510810103], //P5

  // [45.47381395214358, 9.17373819664796], //A1
  // [45.47411291734286, 9.174474977076253], //A3
  // [45.47396622384566, 9.174091122474117], //A2
  // // [45.47391906359549, 9.174165437371892], //A5
  // [45.47386653848617, 9.174254737215302], //A4
  // [45.473602723849424, 9.17403591667373], //A6

  // [45.47348747544734, 9.174180306330408], //X1
  // [45.47391732792696, 9.174735108360915], //X2
  // [45.47369733404852, 9.17509434701203], //X3
  // [45.47359368727235, 9.174585116165316], //X4
  // [45.47326120417173, 9.174561822211754], //X5
];

// Stile del rettangolo
const rectangleStyle = {
  color: 'transparent',
  weight: 0,
  opacity: 0,
  fillColor: 'transparent',
  fillOpacity: 0
};

// Funzione per generare colori casuali vivaci
const generateRandomColor = () => {
  // Genera un colore HSL con:
  // - Hue (tinta): casuale tra 0 e 360
  // - Saturation (saturazione): alta (70-100%) per colori vivaci
  // - Lightness (luminosità): media (45-65%) per buona visibilità
  const hue = Math.random() * 360;
  const saturation = 70 + Math.random() * 30; // 70-100%
  const lightness = 45 + Math.random() * 20;  // 45-65%
  
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

// Funzione per verificare se una posizione è dentro l'area
const isInsideArea = (position) => {
  const [lat, lng] = position;
  const [[northLat, westLng], [southLat, eastLng]] = AREA_BOUNDS;
  return lat <= northLat && lat >= southLat && lng >= westLng && lng <= eastLng;
};

// Icone personalizzate con SVG per maggiore controllo
const userIcon = L.divIcon({
  html: `
    <div class="marker-pulse">
      <svg height="40" width="40" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="4" fill="#0066ff" />
        <circle cx="20" cy="20" r="4" class="ripple-circle" stroke="#0066ff" stroke-width="2" fill="none" />
        <circle cx="20" cy="20" r="4" class="ripple-circle-2" stroke="#0066ff" stroke-width="2" fill="none" />
      </svg>
    </div>`,
  className: 'custom-marker',
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

const otherUserIcon = L.divIcon({
  html: `
    <div class="marker-pulse">
      <svg height="40" width="40" viewBox="0 0 40 40">
        <circle cx="20" cy="20" r="4" fill="white" />
        <circle cx="20" cy="20" r="4" class="ripple-circle" stroke="white" stroke-width="2" fill="none" />
        <circle cx="20" cy="20" r="4" class="ripple-circle-2" stroke="white" stroke-width="2" fill="none" />
      </svg>
    </div>`,
  className: 'other-user-marker',
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

// Funzione per creare l'icona dei marker fissi
const createFixedMarkerIcon = (zoom, baseSize) => {
  const zoomFactor = Math.pow(1.2, zoom - 13);
  const size = Math.round(baseSize * zoomFactor);
  const radius = size * 0.125;
  
  return L.divIcon({
    html: `
      <div class="marker-pulse">
        <svg height="${size}" width="${size}" viewBox="0 0 ${size} ${size}">
          <circle cx="${size/2}" cy="${size/2}" r="${radius}" fill="#808080" />
        </svg>
      </div>`,
    className: 'fixed-marker',
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
  });
};

// Componente per i controlli della mappa
function MapControls({ position, isOutsideArea, markerSize, onMarkerSizeChange }) {
  const map = useMap();

  const centerOnUser = () => {
    if (position) {
      map.flyTo(position, 18, {
        duration: 1
      });
    }
  };

  const showFullArea = () => {
    const center = [
      (AREA_BOUNDS[0][0] + AREA_BOUNDS[1][0]) / 2,
      (AREA_BOUNDS[0][1] + AREA_BOUNDS[1][1]) / 2
    ];
    map.flyTo(center, 13, {
      duration: 1
    });
  };

  const handleSliderStart = (e) => {
    e.stopPropagation();
    map.dragging.disable();
  };

  const handleSliderEnd = (e) => {
    e.stopPropagation();
    map.dragging.enable();
  };

  const handleSliderChange = (e) => {
    e.stopPropagation();
    onMarkerSizeChange(Number(e.target.value));
  };

  return (
    <div className="map-controls">
      <div>
        {!isOutsideArea && (
          <button className="map-button" onClick={centerOnUser}>
            <svg width="20" height="20" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" fill="none" />
              <circle cx="12" cy="12" r="2.5" fill="currentColor" />
              <path d="M12 6L12 0" stroke="currentColor" strokeWidth="2" strokeLinecap="square" />
              <path d="M12 24L12 18" stroke="currentColor" strokeWidth="2" strokeLinecap="square" />
              <path d="M6 12L0 12" stroke="currentColor" strokeWidth="2" strokeLinecap="square" />
              <path d="M24 12L18 12" stroke="currentColor" strokeWidth="2" strokeLinecap="square" />
            </svg>
          </button>
        )}
        {/* <button className="map-button" onClick={showFullArea}>
          Area
        </button> */}
      </div>
      {/* <div className="size-control">
        <span>{markerSize}</span>
        <input
          type="range"
          min="10"
          max="100"
          value={markerSize}
          onChange={handleSliderChange}
          onMouseDown={handleSliderStart}
          onMouseUp={handleSliderEnd}
          onMouseLeave={handleSliderEnd}
          onTouchStart={handleSliderStart}
          onTouchEnd={handleSliderEnd}
        />
      </div> */}
    </div>
  );
}

// Componente per gestire il ridimensionamento dei marker
function ZoomHandler({ setZoomLevel, setIsZooming }) {
  const map = useMapEvent('zoomstart', () => {
    setIsZooming(true);
  });
  
  const map2 = useMapEvent('zoomend', () => {
    setIsZooming(false);
  });
  
  const map3 = useMapEvent('zoom', () => {
    setZoomLevel(map.getZoom());
  });
  
  return null;
}

// Funzione per creare le icone con dimensione basata sul livello di zoom
const createMarkerIcon = (color, zoom, isCurrentUser = false, baseSize) => {
  const zoomFactor = Math.pow(1.2, zoom - 13);
  const size = Math.round(baseSize * zoomFactor);
  
  // Calcola il raggio del cerchio in base alla dimensione
  const radius = size * 0.125; 
  
  return L.divIcon({
    html: `
      <div class="marker-pulse">
        <svg height="${size}" width="${size}" viewBox="0 0 ${size} ${size}">
          <circle cx="${size/2}" cy="${size/2}" r="${radius}" fill="${color}" />
          ${isCurrentUser ? `
            <circle cx="${size/2}" cy="${size/2}" r="${radius}" class="ripple-circle" stroke="${color}" stroke-width="${radius/3}" fill="none" />
            <circle cx="${size/2}" cy="${size/2}" r="${radius}" class="ripple-circle-2" stroke="${color}" stroke-width="${radius/3}" fill="none" />
          ` : ''}
        </svg>
      </div>`,
    className: isCurrentUser ? 'custom-marker' : 'other-user-marker',
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
  });
};

function Map() {
  const [position, setPosition] = useState(null);
  const [otherUsers, setOtherUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOutsideArea, setIsOutsideArea] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(13);
  const [userColor, setUserColor] = useState(null);
  const [isZooming, setIsZooming] = useState(false);
  const [markerSize, setMarkerSize] = useState(25);
  const [geolocationError, setGeolocationError] = useState(null);

  useEffect(() => {
    const socket = io(BACKEND_URL);

    // Assegna un colore casuale all'utente quando si connette
    const randomColor = generateRandomColor();
    setUserColor(randomColor);

    const requestGeolocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const newPosition = [pos.coords.latitude, pos.coords.longitude];
            setPosition(newPosition);
            setIsLoading(false);
            const outsideArea = !isInsideArea(newPosition);
            setIsOutsideArea(outsideArea);
            
            // Se l'utente è dentro l'area, invia la posizione
            if (!outsideArea) {
              socket.emit('updatePosition', {
                position: newPosition,
                color: randomColor
              });
            }
            
            socket.emit('requestPositions');

            // Dopo aver ottenuto i permessi, inizia a tracciare la posizione
            const POSITION_UPDATE_INTERVAL = 2000; // 2 seconds between updates
            let lastUpdate = 0;

            const watchId = navigator.geolocation.watchPosition(
              (pos) => {
                const now = Date.now();
                if (now - lastUpdate >= POSITION_UPDATE_INTERVAL) {
                  lastUpdate = now;
                  const newPosition = [pos.coords.latitude, pos.coords.longitude];
                  setPosition(newPosition);
                  const outsideArea = !isInsideArea(newPosition);
                  setIsOutsideArea(outsideArea);
                  
                  if (!outsideArea) {
                    socket.emit('updatePosition', {
                      position: newPosition,
                      color: randomColor
                    });
                  }
                }
              },
              (error) => {
                console.error('Errore nel tracciamento della posizione:', error);
                setGeolocationError(error.code);
              },
              {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 1000,
                distanceFilter: 2
              }
            );

            // Cleanup function per rimuovere il watch quando il componente viene smontato
            return () => {
              navigator.geolocation.clearWatch(watchId);
            };
          },
          (error) => {
            console.error('Errore nel recupero della posizione:', error);
            setIsLoading(false);
            setGeolocationError(error.code);
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
          }
        );
      } else {
        setGeolocationError(2); // 2 è il codice per "not supported"
      }
    };

    // Richiedi la geolocalizzazione al caricamento
    requestGeolocation();

    // Ascolta gli aggiornamenti delle posizioni degli altri utenti
    socket.on('otherUsersPositions', (users) => {
      // Filtra solo gli utenti che sono dentro l'area
      const usersInsideArea = users.filter(user => isInsideArea(user.position));
      setOtherUsers(usersInsideArea);
    });

    return () => {
      socket.close();
    };
  }, []);

  if (isLoading) {
    return <div className="loading">Caricamento mappa...</div>;
  }

  return (
    <div className="map-container">
      <div className="map-toggle">
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={showMap}
            onChange={(e) => setShowMap(e.target.checked)}
          />
          <span className="toggle-slider"></span>
        </label>
      </div>
      
      {geolocationError === 1 && (
        <div className="outside-area-message">
          Non hai accettato i consensi per la geolocalizzazione.{" "}
          <Link to="/geolocation-guide" className="guide-link" style={{ color: 'white' }}>
            Clicca qui per reimpostarli.
          </Link>
        </div>
      )}
      
      {geolocationError === 2 && (
        <div className="outside-area-message">
          La geolocalizzazione non è supportata dal tuo browser.
        </div>
      )}
      
      {geolocationError === 3 && (
        <div className="outside-area-message">
          Timeout nella richiesta di geolocalizzazione. Ricarica la pagina per riprovare.
        </div>
      )}
      
      {isOutsideArea && (
        <div className="outside-area-message">
          La tua posizione è al di fuori dell'area
        </div>
      )}
      
      <MapContainer
        center={position || [
          (AREA_BOUNDS[0][0] + AREA_BOUNDS[1][0]) / 2,
          (AREA_BOUNDS[0][1] + AREA_BOUNDS[1][1]) / 2
        ]}
        zoom={15}
        style={{ height: '100vh', width: '100%', backgroundColor: '#000000' }}
        minZoom={15}
        maxZoom={18}
        zoomControl={false}
        attributionControl={false}
        maxBounds={MAX_BOUNDS}
        maxBoundsViscosity={1.0}
      >
        <ZoomHandler setZoomLevel={setZoomLevel} setIsZooming={setIsZooming} />
        {showMap && (
          <TileLayer
            url="https://cartodb-basemaps-{s}.global.ssl.fastly.net/dark_all/{z}/{x}/{y}.png"
            attribution='&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="http://cartodb.com/attributions">CartoDB</a>'
          />
        )}
        <Rectangle bounds={AREA_BOUNDS} pathOptions={rectangleStyle} />
        
        {/* Marker fissi */}
        {FIXED_MARKERS.map((position, index) => (
          <Marker
            key={`fixed-${index}`}
            position={position}
            icon={createFixedMarkerIcon(zoomLevel, markerSize)}
            zIndexOffset={100}
          />
        ))}
        
        {/* Markers */}
        {!isOutsideArea && userColor && position && (
          <Marker 
            position={position} 
            icon={createMarkerIcon(userColor, zoomLevel, true, markerSize)}
            zIndexOffset={1000}
          />
        )}
        {otherUsers.map((user, index) => (
          <Marker
            key={user.id || index}
            position={user.position}
            icon={createMarkerIcon(user.color, zoomLevel, false, markerSize)}
            zIndexOffset={900}
          />
        ))}
        <MapControls 
          position={position} 
          isOutsideArea={isOutsideArea} 
          markerSize={markerSize}
          onMarkerSizeChange={setMarkerSize}
        />
      </MapContainer>
    </div>
  );
}

export default Map; 